import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiGrid,
  FiLink2,
  FiLock,
  FiShield,
} from "react-icons/fi";
import logo from "../image/logo.png";
import aboutImage from "../image/landing/designer/Tattqr-img-2-couple-with-tattoo.webp";
import womanImage from "../image/landing/designer/Tattqr-img-1-Lady-with-phone.webp";
import heroV1 from "../image/landing/designer/Tattqr-img-3-tattoo-on-hand.webp";
import heroV2 from "../image/landing/designer/Tatt qr _img 4-creating-tattoo-on-hand.webp";
import ctaImage from "../image/landing/designer/Tatt qr _img 4-creating-tattoo-on-hand.webp";
import patternImage from "../image/landing/designer/bg-patterns.webp";
import vectorFrame from "../image/landing/designer/Vector.svg";
import "./LandingPage.css";

const heroSlides = [
  {
    id: "hero-v1",
    image: heroV1,
    line1: "Your Tattoo",
    line2Prefix: "Your Story",
    line2Highlight: " Unlock It",
    line3: "with a Scan",
    body:
      "Create a QR tattoo that connects to your socials, portfolio, or anything you want and update it anytime without changing the ink.",
  },
  {
    id: "hero-v2",
    image: heroV2,
    line1: "Create a Scannable QR",
    line2Prefix: "",
    line2Highlight: " ",
    line3: "Connects to Anything",
    body:
      "Create a QR tattoo that connects to your socials, portfolio, or anything you want and update it anytime without changing the ink.",
  },
];

const serviceItems = [
  {
    title: "Digital Identity",
    description:
      "Transform static ink into a dynamic multimedia experience. Scan your tattoo.",
    icon: FiShield,
  },
  {
    title: "Interaction System",
    description:
      "Transform static ink into a dynamic multimedia experience. Scan your tattoo.",
    icon: FiLink2,
  },
  {
    title: "Access Control",
    description:
      "Transform static ink into a dynamic multimedia experience. Scan your tattoo.",
    icon: FiLock,
  },
  {
    title: "Creator Dashboard",
    description:
      "Transform static ink into a dynamic multimedia experience. Scan your tattoo.",
    icon: FiGrid,
  },
];

const featurePoints = [
  {
    title: "Exclusivity Guaranteed",
    text: "Every design is registered as a unique NFT, ensuring your art remains yours alone.",
  },
  {
    title: "Organic Luxury Inks",
    text: "We use proprietary, time-friendly premium formulas optimized for digital recognition.",
  },
  {
    title: "Updateable Content",
    text: "The ink stays the same, but you can change the digital content behind it anytime.",
  },
];

const testimonialItems = [
  {
    id: "t1",
    quote:
      "Our wedding vows now live on our skin and in our story. Every scan brings us back to that exact moment.",
    name: "Jenny Kim",
    role: "Philanthropist",
  },
  {
    id: "t2",
    quote:
      "I linked my portfolio to one tattoo and update it whenever I launch new work. Clients love the experience.",
    name: "Marcus Vale",
    role: "Creative Director",
  },
  {
    id: "t3",
    quote:
      "The secure PIN option lets me share private family memories only with people I trust.",
    name: "Elena Torres",
    role: "Founder",
  },
  {
    id: "t4",
    quote:
      "We used temporary content for our event campaign and switched it instantly after launch week. Super smooth.",
    name: "Noah Bennett",
    role: "Event Producer",
  },
  {
    id: "t5",
    quote:
      "I thought this would be a gimmick, but it became part of my brand identity. The scans keep growing every month.",
    name: "Aisha Rahman",
    role: "Artist",
  },
  {
    id: "t6",
    quote:
      "My QR tattoo now opens a voice note from my dad. It is personal, emotional, and beautifully preserved.",
    name: "Daniel Cho",
    role: "Architect",
  },
  {
    id: "t7",
    quote:
      "From profile links to launch announcements, everything is controlled from one dashboard. Simple and elegant.",
    name: "Sofia Grant",
    role: "Product Lead",
  },
  {
    id: "t8",
    quote:
      "Our studio now offers TattQR as a premium package. It changed how clients think about tattoo storytelling.",
    name: "Luca Moretti",
    role: "Tattoo Studio Owner",
  },
  {
    id: "t9",
    quote:
      "The first time someone scanned my tattoo and saw my music reel, I knew this was the future of creator links.",
    name: "Priya Nair",
    role: "Music Creator",
  },
];

const navItems = [
  { label: "About Us", path: "/about-us" },
  { label: "Services", path: "/services" },
  { label: "How It Works", path: "/how-it-works" },
  { label: "Testimonials", path: "/testimonials" },
  { label: "Contact", path: "/contact-us" },
];

const getTestimonialCardsPerView = () => {
  if (typeof window === "undefined") {
    return 3;
  }
  if (window.innerWidth <= 760) {
    return 1;
  }
  if (window.innerWidth <= 1100) {
    return 2;
  }
  return 3;
};

const LandingPage = () => {
  const navigate = useNavigate();
  const [activeSlide, setActiveSlide] = useState(0);
  const [testimonialCardsPerView, setTestimonialCardsPerView] = useState(
    getTestimonialCardsPerView
  );
  const [testimonialStartIndex, setTestimonialStartIndex] = useState(0);
  const slide = useMemo(() => heroSlides[activeSlide], [activeSlide]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveSlide((prev) => (prev + 1) % heroSlides.length);
    }, 6000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const preloaders = heroSlides.map(({ image }) => {
      const preloaded = new Image();
      preloaded.decoding = "async";
      preloaded.src = image;
      return preloaded;
    });

    return () => {
      preloaders.forEach((preloaded) => {
        preloaded.src = "";
      });
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setTestimonialCardsPerView(getTestimonialCardsPerView());
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    setTestimonialStartIndex((prev) => prev % testimonialItems.length);
  }, [testimonialCardsPerView]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setTestimonialStartIndex(
        (prev) => (prev + testimonialCardsPerView) % testimonialItems.length
      );
    }, 5500);

    return () => window.clearInterval(interval);
  }, [testimonialCardsPerView]);

  const jumpToSlide = (index) => {
    setActiveSlide(index);
  };

  const visibleTestimonials = useMemo(() => {
    return Array.from({ length: testimonialCardsPerView }, (_, offset) => {
      const testimonialIndex =
        (testimonialStartIndex + offset) % testimonialItems.length;
      return testimonialItems[testimonialIndex];
    });
  }, [testimonialCardsPerView, testimonialStartIndex]);

  const moveTestimonials = (direction) => {
    setTestimonialStartIndex((prev) => {
      const next =
        prev + direction * testimonialCardsPerView + testimonialItems.length;
      return next % testimonialItems.length;
    });
  };

  const testimonialPages = Math.ceil(
    testimonialItems.length / testimonialCardsPerView
  );
  const activeTestimonialPage = Math.floor(
    testimonialStartIndex / testimonialCardsPerView
  );

  const handleContactSubmit = (event) => {
    event.preventDefault();
  };

  return (
    <div className="lp2" style={{ "--lp2-pattern-image": `url(${patternImage})` }}>
      <section
        className={`lp2-hero ${slide.id === "hero-v2" ? "lp2-hero-alt" : ""}`}
        style={{ backgroundImage: `url(${slide.image})` }}
        id="top"
      >
        <div className="lp2-hero-overlay" />
        <header className="lp2-nav">
          <div className="lp2-shell lp2-nav-inner">
            <button
              type="button"
              className="lp2-logo-button"
              onClick={() => navigate("/landing")}
              aria-label="Go to landing top"
            >
              <img src={logo} alt="TattQR logo" />
            </button>
            <nav className="lp2-links">
              {navItems.map((item) => (
                <button
                  key={item.path}
                  type="button"
                  className="lp2-nav-link"
                  onClick={() => navigate(item.path)}
                >
                  {item.label}
                </button>
              ))}
            </nav>
            <div className="lp2-actions">
              <button
                type="button"
                className="lp2-login-btn"
                onClick={() => navigate("/login")}
              >
                Login
              </button>
              <button
                type="button"
                className="lp2-gold-btn"
                onClick={() => navigate("/signup")}
              >
                Design My QR
              </button>
            </div>
          </div>
        </header>

        <div className="lp2-shell lp2-hero-content">
          <div className="lp2-hero-grid">
            <div className="lp2-hero-copy" key={slide.id}>
              <h1>
                <span>{slide.line1}</span>
                <span>
                  {slide.line2Prefix}
                  <em>{slide.line2Highlight}</em>
                </span>
                <span>
                  <em>{slide.line3}</em>
                </span>
              </h1>
              <p>{slide.body}</p>
              <button
                type="button"
                className="lp2-gold-btn lp2-primary-cta"
                onClick={() => navigate("/signup")}
              >
                Create My QR Tattoo
              </button>
              <small>Works with any device, no app required</small>
              <div className="lp2-slide-controls">
                {heroSlides.map((heroSlide, index) => (
                  <button
                    key={heroSlide.id}
                    type="button"
                    className={`lp2-slide-dot ${index === activeSlide ? "active" : ""}`}
                    onClick={() => jumpToSlide(index)}
                    aria-label={`Show hero slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="lp2-pattern lp2-section lp2-about-wrap" id="about">
        <div className="lp2-shell lp2-about">
          <div className="lp2-about-image">
            <img src={aboutImage} alt="People sharing tattoo story" />
          </div>
          <div className="lp2-about-copy">
            <p className="lp2-kicker">About Us</p>
            <h2>Bridging the Gap Between Permanent Art and Digital Storytelling</h2>
            <p>
              At TattQR, tattoos are more than ink on skin. They are stories,
              memories, and moments that deserve a living home. Our platform
              lets your tattoo connect to content like video, audio, photos,
              and messages, so every scan reveals the meaning behind the art.
            </p>
            <div className="lp2-inline-actions">
              <button
                type="button"
                className="lp2-outline-btn"
                onClick={() => navigate("/contact-us")}
              >
                Contact Us
              </button>
              <button
                type="button"
                className="lp2-gold-btn lp2-small-btn"
                onClick={() => navigate("/signup")}
              >
                Get QR Now
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="lp2-pattern lp2-section lp2-services-wrap" id="services">
        <div className="lp2-shell">
          <p className="lp2-kicker lp2-center">Our Services</p>
          <h2 className="lp2-center">Ecosystem Of Memory</h2>
          <div className="lp2-services-grid">
            {serviceItems.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title} className="lp2-service-card">
                  <img src={vectorFrame} className="lp2-card-frame" alt="" aria-hidden="true" />
                  <div className="lp2-service-card-content">
                    <div className="lp2-service-icon">
                      <Icon />
                    </div>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="lp2-pattern lp2-section lp2-why-wrap">
        <div className="lp2-shell lp2-why">
          <div className="lp2-why-image">
            <img src={womanImage} alt="TattQR model with tattoo" />
          </div>
          <div className="lp2-why-copy">
            <p className="lp2-kicker">Why TattQR?</p>
            <div className="lp2-why-headline">
              <h2>The Standard In Digital Memory</h2>
              <button
                type="button"
                className="lp2-gold-btn lp2-small-btn"
                onClick={() => navigate("/signup")}
              >
                Get QR Now
              </button>
            </div>
            <div className="lp2-features-grid">
              {featurePoints.map((point) => (
                <article key={point.title} className="lp2-feature-item">
                  <span className="lp2-feature-icon">
                    <FiCheckCircle />
                  </span>
                  <div>
                    <h3>{point.title}</h3>
                    <p>{point.text}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="lp2-section lp2-steps" id="how-it-works">
        <div className="lp2-shell">
          <p className="lp2-kicker lp2-center">How It Works?</p>
          <h2 className="lp2-center">Three Steps To TattQR</h2>
          <div className="lp2-steps-grid">
            <article className="lp2-step-card">
              <div className="lp2-step-index">1</div>
              <h3>Consult &amp; Create</h3>
              <p>
                Share your story. We design a custom tattoo and link it to your
                chosen digital content.
              </p>
            </article>
            <article className="lp2-step-card">
              <div className="lp2-step-index">2</div>
              <h3>Elite Inking</h3>
              <p>
                Our certified TattQR artists bring the design to life with
                precision and premium equipment.
              </p>
            </article>
            <article className="lp2-step-card">
              <div className="lp2-step-index">3</div>
              <h3>Scan &amp; Relive</h3>
              <p>
                Simply open the TattQR app, scan your ink, and watch your
                memories unfold in AR.
              </p>
            </article>
            <svg
              className="lp2-steps-connectors"
              viewBox="0 0 1200 120"
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <marker
                  id="lp2CurvyArrow"
                  markerWidth="6"
                  markerHeight="6"
                  refX="5"
                  refY="3"
                  orient="auto"
                  markerUnits="strokeWidth"
                >
                  <path d="M0.5,0.5 L5.5,3 L0.5,5.5" />
                </marker>
              </defs>
              <path d="M 246 34 C 334 22, 430 24, 556 52" markerEnd="url(#lp2CurvyArrow)" />
              <path d="M 666 52 C 754 72, 848 72, 966 34" markerEnd="url(#lp2CurvyArrow)" />
            </svg>
          </div>
          <button
            type="button"
            className="lp2-gold-btn lp2-centered-btn"
            onClick={() => navigate("/signup")}
          >
            Get Your QR Now
          </button>
        </div>
      </section>

      <section className="lp2-pattern lp2-section lp2-testimonials-wrap" id="testimonials">
        <div className="lp2-shell">
          <p className="lp2-kicker lp2-center">Testimonials</p>
          <h2 className="lp2-center">A New Era Of Storytelling</h2>
          <div className="lp2-testimonial-row">
            <button
              type="button"
              className="lp2-nav-circle"
              aria-label="Previous"
              onClick={() => moveTestimonials(-1)}
            >
              <FiArrowLeft />
            </button>
            <div
              key={`testimonials-${testimonialStartIndex}-${testimonialCardsPerView}`}
              className="lp2-testimonial-grid"
              style={{ "--lp2-visible-cards": testimonialCardsPerView }}
            >
              {visibleTestimonials.map((item) => (
                <article key={item.id} className="lp2-quote-card">
                  <img src={vectorFrame} className="lp2-card-frame" alt="" aria-hidden="true" />
                  <div className="lp2-quote-card-content">
                    <p>&ldquo;{item.quote}&rdquo;</p>
                    <div className="lp2-quote-meta">
                      <span className="lp2-quote-avatar" />
                      <h3>{item.name}</h3>
                      <small>{item.role.toUpperCase()}</small>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <button
              type="button"
              className="lp2-nav-circle"
              aria-label="Next"
              onClick={() => moveTestimonials(1)}
            >
              <FiArrowRight />
            </button>
          </div>
          <div className="lp2-testimonial-pagination" aria-hidden="true">
            {Array.from({ length: testimonialPages }, (_, index) => (
              <span
                key={`testimonial-page-${index}`}
                className={`lp2-testimonial-dot ${index === activeTestimonialPage ? "active" : ""}`}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="lp2-pattern lp2-section">
        <div className="lp2-shell">
          <div
            className="lp2-cta-banner"
            style={{ "--lp2-cta-image": `url(${ctaImage})` }}
          >
            <div className="lp2-cta-overlay" />
            <div className="lp2-cta-content">
              <h2>READY TO WRITE YOUR STORY IN LIGHT?</h2>
              <p>
                Join the elite few who are redefining what it means to wear
                your heart on your sleeve.
              </p>
              <button
                type="button"
                className="lp2-gold-btn lp2-small-btn"
                onClick={() => navigate("/contact-us")}
              >
                Book Exclusive Consultation
              </button>
            </div>
          </div>
        </div>
      </section>

      <section className="lp2-section lp2-contact-wrap" id="contact">
        <div className="lp2-shell lp2-contact">
          <div className="lp2-contact-copy">
            <h2>Contact US</h2>
            <p>
               Ready to turn ink into an experience? Share your concept and our team
          will guide you from QR strategy to final scan-ready delivery. Contact our team for further information on TattQR. Contact our
              team for further information on TattQR.
            </p>
            <ul>
              <li>CA, USA</li>
              <li>tattqr@gmail.com</li>
            </ul>
          </div>
          <form className="lp2-contact-form" onSubmit={handleContactSubmit}>
            <div className="lp2-contact-row">
              <input type="text" placeholder="Full Name" />
              <input type="email" placeholder="Email Address" />
            </div>
            <input type="text" placeholder="Subject" />
            <textarea placeholder="Tell us your story..." rows={5} />
            <button type="submit" className="lp2-gold-btn lp2-submit-btn">
              Send Query
            </button>
          </form>
        </div>
      </section>

      <footer className="lp2-footer">
        <div className="lp2-shell lp2-footer-inner">
          <div className="lp2-footer-brand">
            <img src={logo} alt="TattQR logo" />
            <small>&copy; 2026 TattQR. All rights reserved.</small>
          </div>
          <nav className="lp2-footer-links">
            {navItems.map((item) => (
              <button
                key={item.path}
                type="button"
                className="lp2-footer-nav-btn"
                onClick={() => navigate(item.path)}
              >
                {item.label}
              </button>
            ))}
          </nav>
          <div className="lp2-footer-legal">
            <Link to="/privacy-policy">Privacy Policy</Link>
            <Link to="/terms-of-service">Terms of Service</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
